import { useMemo } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/Button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Logo } from '@/components/Logo'
import { useAuth } from '@/features/auth/AuthContext'
import { rememberReturnTo, takeReturnTo } from '@/features/auth/returnTo'
import { env } from '@/lib/env'
import { isApiError } from '@/lib/problem'
import { hashString } from '@/lib/utils'

/** The Microsoft logo mark, inlined so no third-party asset is fetched. */
function MicrosoftMark() {
  return (
    <span aria-hidden className="grid size-4 shrink-0 grid-cols-2 grid-rows-2 gap-[2px]">
      <span className="bg-current opacity-90" />
      <span className="bg-current opacity-[.55]" />
      <span className="bg-current opacity-[.55]" />
      <span className="bg-current opacity-90" />
    </span>
  )
}

/**
 * The brand's geometric pattern, read as a shelf of book spines: bars in the
 * guideline's palette at varying widths and heights, raked off-axis. Derived
 * deterministically so it never reflows between renders.
 */
function SpinePattern() {
  const spines = useMemo(() => {
    const fills = [
      'rgb(var(--itpu-sharp-blue))',
      'rgb(var(--itpu-light-blue))',
      'rgb(var(--itpu-bright-blue))',
      'rgb(var(--itpu-lime))',
      'rgb(var(--itpu-coral))',
      'rgb(var(--itpu-white))',
    ]
    return Array.from({ length: 16 }, (_, i) => {
      const h = hashString(`spine-${i}`)
      return {
        key: i,
        background: fills[h % fills.length] as string,
        width: 18 + (h % 26),
        height: `${62 + (h % 34)}%`,
        marginTop: `${(h >> 3) % 16}%`,
      }
    })
  }, [])

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 flex gap-[10px] overflow-hidden py-10 opacity-[.16]"
      style={{ transform: 'rotate(-8deg) scale(1.25)' }}
    >
      {spines.map((spine) => (
        <span
          key={spine.key}
          className="block shrink-0 rounded-sm"
          style={{
            background: spine.background,
            width: spine.width,
            height: spine.height,
            marginTop: spine.marginTop,
          }}
        />
      ))}
    </div>
  )
}

/**
 * §12.1 — one decision, zero friction, and an unmistakable statement that this
 * library belongs to ITPU. The deep link the visitor originally asked for is
 * preserved in location state and restored after sign-in (FR-1.4).
 */
export default function SignInPage() {
  const { t } = useTranslation(['auth', 'common', 'errors'])
  const { status, signIn, error } = useAuth()
  const location = useLocation()

  if (status === 'signed-in') {
    // `location.state` covers a navigation that stayed inside the app; the
    // stored path covers the one that went out to Entra and back, which is the
    // case a shared link actually takes.
    const from = (location.state as { from?: Location } | null)?.from
    return <Navigate to={from?.pathname ?? takeReturnTo() ?? '/'} replace />
  }
  if (status === 'not-permitted') return <Navigate to="/403" replace />

  const redirecting = status === 'signing-in'
  const traceId = isApiError(error) ? error.traceId : undefined

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <section className="relative flex shrink-0 flex-col justify-between overflow-hidden bg-itpu-navy px-6 py-10 lg:min-h-screen lg:w-[55%] lg:px-14 lg:py-14">
        <SpinePattern />

        <div className="relative">
          <Logo variant="full" tone="white" size={34} />
        </div>

        <div className="relative mt-10 max-w-[460px] lg:mt-0">
          <h1 className="text-h1 text-itpu-white">{t('signInHeading')}</h1>
          <p className="mt-4 text-body-lg leading-[1.6] text-itpu-white/[.72]">
            {t('signInTagline', { books: 371, subjects: 73 })}
          </p>
        </div>

        <p className="relative mt-10 text-label uppercase tracking-wider text-itpu-white/50 lg:mt-0">
          library.itpu.uz
        </p>
      </section>

      <section className="relative flex flex-1 flex-col px-4 py-6 lg:px-10 lg:py-8">
        <div className="flex justify-end">
          <LanguageSwitcher />
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-[420px]">
            {/* The guideline's short teal rule above a heading. */}
            <span aria-hidden className="mb-6 block h-[3px] w-[37px] bg-itpu-teal" />

            <h2 className="text-h2">{t('signInTitle')}</h2>
            <p className="mt-4 text-body text-text-body">
              {t('signInSubtext', { books: 371, subjects: 73 })}
            </p>

            {status === 'error' && (
              <div
                role="alert"
                className="mt-5 flex items-start gap-2 rounded-sm border border-danger/30 bg-danger-tint p-3 text-meta text-danger"
              >
                <AlertTriangle aria-hidden className="mt-[2px] size-4 shrink-0" strokeWidth={2} />
                <div>
                  <p>{t('signInFailed')}</p>
                  {traceId && (
                    <p className="mt-1 font-mono text-text-muted">
                      {t('errors:traceId', { traceId })}
                    </p>
                  )}
                </div>
              </div>
            )}

            <Button
              variant="navy"
              size="lg"
              fullWidth
              className="mt-7"
              loading={redirecting}
              leadingIcon={redirecting ? undefined : <MicrosoftMark />}
              onClick={() => {
                // Written here, in the click, because this is the last moment
                // the app runs before the browser leaves for Entra.
                const from = (location.state as { from?: Location } | null)?.from
                if (from) rememberReturnTo(`${from.pathname}${from.search ?? ''}`)
                void signIn()
              }}
            >
              {redirecting ? t('redirecting') : t('signIn')}
            </Button>

            <p className="mt-4 text-meta text-text-muted">{t('domainNote')}</p>

            <div className="mt-8 border-t border-border pt-5 text-meta text-text-muted">
              {t('noAccess')}{' '}
              <Link to="/403" className="text-text-link hover:underline">
                {t('seeRefusalScreen')}
              </Link>
            </div>

            <p className="mt-3 text-meta">
              <a href={`mailto:${env.contactEmail}`} className="text-text-link hover:underline">
                {t('common:footer.contact')}
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
